import { ChangeEvent, FocusEvent, useEffect, useState } from "react";
import {
    TextField,
    Tooltip,
    Typography,
    Box,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    useTheme,
    Alert,
    Tabs,
    Tab
} from "@mui/material";
import ListAltIcon from '@mui/icons-material/ListAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QRCode from "react-qr-code";
import JsonView from "@uiw/react-json-view";
import { vscodeTheme } from "@uiw/react-json-view/vscode";
import { lightTheme } from "@uiw/react-json-view/light";
import { useAuth } from "../context/AuthContext";
import LicenseDetails from "./LicenseDetails";

export type LicenseCtrlProps = {
    value: string;
    name?: string;
    label?: string;
    helperText?: string;
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onBlur: (e: FocusEvent<any>) => void;
};

const LicenseCtrl = ({
    value,
    name = "token",
    label = "License token",
    helperText = "Provide your license token",
    onChange,
    onBlur
}: LicenseCtrlProps) => {
    const { authorizedFetch, resolveApiPath } = useAuth();
    const theme = useTheme();
    const [nodeInfo, setNodeInfo] = useState<string | null>(null);
    const [showLicenseDialog, setShowLicenseDialog] = useState(false);
    const [uploadLicenseDialog, setUploadLicenseDialog] = useState(false);
    const [licenseData, setLicenseData] = useState<any>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isFileValid, setIsFileValid] = useState(false);
    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
    const [licenseTabValue, setLicenseTabValue] = useState(0);
    const [verificationError, setVerificationError] = useState<string | null>(null);
    const [isCopied, setIsCopied] = useState(false);

    const jsonViewTheme = theme.palette.mode === "dark" ? vscodeTheme : lightTheme;

    useEffect(() => {
        let cancelled = false;

        const fetchNodeInfo = async () => {
            try {
                const response = await authorizedFetch(
                    resolveApiPath("/api/config/nodeinfo")
                );
                if (!response.ok) {
                    return;
                }

                const payload = (await response
                    .json()
                    .catch(() => null)) as { nodeInfo?: string } | null;

                if (!cancelled) {
                    setNodeInfo(payload?.nodeInfo ?? null);
                }
            } catch {
                // Ignore network failures for optional node info
            }
        };

        fetchNodeInfo();
        // Also fetch license data on mount to check if it exists
        fetchLicenseData();

        return () => {
            cancelled = true;
        };
    }, [authorizedFetch, resolveApiPath]);

    const decodeJWT = (token: string): any => {
        try {
            // JWT format: header.payload.signature
            const parts = token.split('.');
            if (parts.length !== 3) {
                return null;
            }
            // Decode the payload (second part)
            const payload = parts[1];
            const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
            return JSON.parse(decoded);
        } catch {
            return null;
        }
    };

    const fetchLicenseData = async () => {
        try {
            const response = await authorizedFetch(
                resolveApiPath("/api/config/license")
            );
            if (!response.ok) {
                return;
            }
            const data = await response.json().catch(() => null);

            // If we have a token, decode it to show the payload
            if (data?.token) {
                const decodedPayload = decodeJWT(data.token);
                setLicenseData({
                    token: data.token,
                    decoded: decodedPayload,
                    valid: decodedPayload !== null && !data.verificationError
                });

                // Set verification error from backend
                if (data.verificationError) {
                    setVerificationError(data.verificationError);
                } else {
                    setVerificationError(null);
                }
            } else {
                setLicenseData(null);
                setVerificationError(null);
            }
        } catch {
            // Ignore network failures
        }
    };

    const handleShowLicense = () => {
        fetchLicenseData();
        setLicenseTabValue(0); // Reset to first tab
        setShowLicenseDialog(true);
    };

    const handleUploadLicense = () => {
        setSelectedFile(null);
        setUploadError(null);
        setIsFileValid(false);
        setVerificationError(null);
        setUploadLicenseDialog(true);
    };

    const validateFile = (file: File): string | null => {
        // Check file extension
        if (!file.name.toLowerCase().endsWith('.lic')) {
            return 'Only .lic files are allowed';
        }
        return null;
    };

    const validateJWT = (token: string): boolean => {
        // JWT must have exactly 3 parts separated by dots
        const parts = token.trim().split('.');
        if (parts.length !== 3) {
            return false;
        }
        // Each part should be non-empty
        if (parts.some(part => !part)) {
            return false;
        }
        // Try to decode the payload to verify it's valid base64
        try {
            const payload = parts[1];
            atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
            return true;
        } catch {
            return false;
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const error = validateFile(file);
            if (error) {
                setUploadError(error);
                setSelectedFile(null);
                setIsFileValid(false);
                event.target.value = ''; // Reset input
                return;
            }
            setSelectedFile(file);
            setUploadError(null);
            // Validate JWT content
            validateFileContent(file);
        }
    };

    const validateFileContent = async (file: File) => {
        try {
            const content = await file.text();
            const trimmedContent = content.trim();

            if (!validateJWT(trimmedContent)) {
                setUploadError('Invalid license file: Content must be a valid JWT token');
                setIsFileValid(false);
                return;
            }

            // Decode and validate issuer and product name
            const decoded = decodeJWT(trimmedContent);
            if (decoded) {
                // Check issuer
                if (decoded.iss !== 'ImpleoTV') {
                    setUploadError(`Invalid license issuer: expected 'ImpleoTV', got '${decoded.iss || 'undefined'}'`);
                    setIsFileValid(false);
                    return;
                }

                // Check product name
                const productName = decoded.product?.name;
                if (productName !== undefined && productName !== 'SkyView') {
                    setUploadError(`This license is not for SkyView application (product: ${productName})`);
                    setIsFileValid(false);
                    return;
                } else if (decoded.product && !productName) {
                    setUploadError('This license is for an unknown application');
                    setIsFileValid(false);
                    return;
                }
                // If product doesn't exist at all or product.name === 'SkyView', it's valid
            } else {
                // JWT format is valid but can't decode payload (shouldn't happen if validateJWT passed)
                setUploadError('Invalid license file: Cannot decode JWT payload');
                setIsFileValid(false);
                return;
            }

            setUploadError(null);
            setIsFileValid(true);
        } catch (error) {
            setUploadError('Failed to read file content');
            setIsFileValid(false);
        }
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragging(false);
        const file = event.dataTransfer.files?.[0];
        if (file) {
            const error = validateFile(file);
            if (error) {
                setUploadError(error);
                setSelectedFile(null);
                setIsFileValid(false);
                return;
            }
            setSelectedFile(file);
            setUploadError(null);
            // Validate JWT content
            validateFileContent(file);
        }
    };

    const handleUploadFile = async () => {
        if (!selectedFile || !isFileValid) return;

        setUploadError(null);
        setVerificationError(null);

        try {
            // Read the file content as text
            const fileContent = await selectedFile.text();
            const trimmedContent = fileContent.trim();

            const response = await authorizedFetch(
                resolveApiPath("/api/config/license"),
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({ token: trimmedContent })
                }
            );

            if (response.ok) {
                // Close dialog and clear states
                setUploadLicenseDialog(false);
                setSelectedFile(null);
                setUploadError(null);
                setIsFileValid(false);
                setVerificationError(null);
                // Refresh license data to show the new license
                fetchLicenseData();
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error("Failed to upload license:", errorData);

                // Close the upload dialog
                setUploadLicenseDialog(false);
                setSelectedFile(null);
                setUploadError(null);
                setIsFileValid(false);

                // Refresh license data to show the uploaded (but invalid) license
                fetchLicenseData();

                // Show verification error at the bottom
                if (errorData.details) {
                    setVerificationError(`${errorData.error}: ${errorData.details}`);
                } else {
                    setVerificationError(errorData.error || 'Failed to upload license');
                }
            }
        } catch (error) {
            console.error("Failed to upload license:", error);
            // Close dialog on network error
            setUploadLicenseDialog(false);
            setSelectedFile(null);
            setUploadError(null);
            setIsFileValid(false);
            setVerificationError('Failed to upload license. Please try again.');
        }
    };

    const handleCancelUpload = () => {
        setUploadLicenseDialog(false);
        setSelectedFile(null);
        setUploadError(null);
        setIsFileValid(false);
    };

    const handleDeleteLicense = () => {
        setDeleteConfirmDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            const response = await authorizedFetch(
                resolveApiPath("/api/config/license"),
                {
                    method: "DELETE"
                }
            );

            if (response.ok) {
                setDeleteConfirmDialog(false);
                setVerificationError(null);
                // Refresh license data to reflect removal
                fetchLicenseData();
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error("Failed to delete license:", errorData);
                alert(`Failed to delete license: ${errorData.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error("Failed to delete license:", error);
            alert("Failed to delete license. Please try again.");
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirmDialog(false);
    };

    // Check if license token exists (must be declared before use)
    const hasLicense = licenseData?.token && licenseData.token.trim() !== '';

    // Determine if license is expired for chip display
    let isExpired = false;
    if (hasLicense && licenseData?.decoded?.exp) {
        const expDate = new Date(licenseData.decoded.exp * 1000);
        const now = new Date();
        isExpired = now > expDate;
    }

    // Determine if license is valid (decoded successfully)
    const isValid = hasLicense && licenseData?.valid === true;

    return (<>
        <Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "flex-start" },
                justifyContent: "center",
                gap: { xs: 2, sm: 3 },
                width: "100%",
                boxSizing: "border-box"
            }}
        >
            {/* Left: QR code - centered if no license */}
            <Box
                sx={{
                    minWidth: { sm: 120 },
                    width: { xs: "100%", sm: "auto" },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1,
                    flex: hasLicense ? "0 0 auto" : 1
                }}
            >
                {nodeInfo !== null && (
                    <Box
                        sx={{
                            mt: 1,
                            backgroundColor: "white",
                            p: 1,
                            borderRadius: 1,
                            border: "2px solid",
                            borderColor: "divider",
                            display: "inline-flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            boxSizing: "border-box",
                            width: { xs: "100%", sm: "auto" },
                            maxWidth: "100%"
                        }}
                    >
                        <QRCode
                            size={148}
                            value={nodeInfo}
                            viewBox="0 0 256 256"
                            style={{ display: "block" }}
                        />
                    </Box>
                )}
                {/* intentionally left blank here; nodeInfo will be shown below the layout */}
            </Box>

            {/* only show if license exists */}
            {hasLicense && licenseData?.decoded && (
                <LicenseDetails decodedLicense={licenseData.decoded} onBlur={onBlur} />
            )}
        </Box>

        {nodeInfo !== null && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.25, width: '100%' }}>
                <Typography
                    variant="body2"
                    align="left"
                    color="text.secondary"
                    style={{ wordBreak: "break-all", flex: 1 }}
                >
                    Node Info:{' '}
                    <span style={{ color: theme.palette.primary.main }}>{nodeInfo}</span>
                </Typography>
                <Tooltip title="Copy to clipboard">
                    <IconButton
                        size="small"
                        onClick={() => {
                            navigator.clipboard.writeText(nodeInfo);
                            setIsCopied(true);
                            setTimeout(() => setIsCopied(false), 1000);
                        }}
                        sx={{ ml: 0.5, flexShrink: 0 }}
                    >
                        {isCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
            </Box>
        )}
        <Box style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, width: '100%' }}>
            {/* Show chip only when a license exists. Priority: invalid > expired > valid */}
            {hasLicense ? (
                !isValid ? (
                    <Chip label="Invalid" color="error" icon={<WarningAmberIcon />} variant="filled" style={{ height: 32, width: 120, fontWeight: 500 }} />
                ) : isExpired ? (
                    <Chip label="Expired" color="error" icon={<WarningAmberIcon />} variant="filled" style={{ height: 32, width: 120, fontWeight: 500 }} />
                ) : (
                    <Chip label="License OK" color="success" icon={<CheckIcon />} variant="filled" style={{ height: 32, width: 120, fontWeight: 500 }} />
                )
            ) : null}
            <div style={{ flex: 1 }} />
            {hasLicense && (
                <Tooltip title="Show license">
                    <IconButton size="small" aria-label="details" onClick={handleShowLicense}>
                        <ListAltIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            <Tooltip title="Upload license">
                <IconButton size="small" aria-label="upload" onClick={handleUploadLicense}>
                    <CloudUploadIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            {hasLicense && (
                <Tooltip title="Remove license">
                    <IconButton size="small" aria-label="delete" onClick={handleDeleteLicense}>
                        <DeleteForeverIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </Box>

        {/* Verification Error Alert */}
        {verificationError && (
            <Alert
                severity="error"
                onClose={() => setVerificationError(null)}
                sx={{ mt: 2, width: '100%' }}
            >
                {verificationError}
            </Alert>
        )}

        {/* Show License Dialog */}
        <Dialog
            open={showLicenseDialog}
            onClose={() => setShowLicenseDialog(false)}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>License Details</DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                {licenseData ? (
                    licenseData.decoded ? (
                        <Box sx={{ width: '100%' }}>
                            <Tabs
                                value={licenseTabValue}
                                onChange={(_, newValue) => setLicenseTabValue(newValue)}
                                sx={{ borderBottom: 1, borderColor: 'divider' }}
                            >
                                <Tab label="License Payload" />
                                <Tab label="Raw JWT Token" />
                            </Tabs>

                            {/* Tab Panel 0: Decoded License Payload */}
                            <Box role="tabpanel" hidden={licenseTabValue !== 0} sx={{ p: 3 }}>
                                {licenseTabValue === 0 && (
                                    <JsonView
                                        value={licenseData.decoded}
                                        style={jsonViewTheme}
                                        collapsed={false}
                                        displayDataTypes={false}
                                    />
                                )}
                            </Box>

                            {/* Tab Panel 1: Raw JWT Token */}
                            <Box role="tabpanel" hidden={licenseTabValue !== 1} sx={{ p: 3 }}>
                                {licenseTabValue === 1 && (
                                    <TextField
                                        value={licenseData.token}
                                        multiline
                                        rows={10}
                                        fullWidth
                                        InputProps={{ readOnly: true }}
                                        size="small"
                                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                    />
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" color="warning.main" gutterBottom>
                                Invalid JWT format - showing raw token:
                            </Typography>
                            <TextField
                                value={licenseData.token}
                                multiline
                                rows={4}
                                fullWidth
                                InputProps={{ readOnly: true }}
                                size="small"
                                sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                            />
                        </Box>
                    )
                ) : (
                    <Box sx={{ p: 3 }}>
                        <Typography color="text.secondary">No license data available</Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowLicenseDialog(false)}>Close</Button>
            </DialogActions>
        </Dialog>

        {/* Upload License Dialog */}
        <Dialog
            open={uploadLicenseDialog}
            onClose={handleCancelUpload}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>Upload License</DialogTitle>
            <DialogContent dividers>
                <Box
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                        border: 2,
                        borderStyle: 'dashed',
                        borderColor: isDragging ? 'primary.main' : uploadError ? 'error.main' : 'divider',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: isDragging ? 'action.hover' : 'background.paper',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'action.hover'
                        }
                    }}
                    onClick={() => document.getElementById('license-file-input')?.click()}
                >
                    <input
                        id="license-file-input"
                        type="file"
                        accept=".lic"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body1" gutterBottom>
                        {selectedFile ? selectedFile.name : 'Drop .lic file here or click to browse'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Only .lic files containing valid JWT tokens are accepted
                    </Typography>
                </Box>

                {uploadError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {uploadError}
                    </Alert>
                )}

                {isFileValid && !uploadError && selectedFile && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        License file validated successfully
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelUpload}>Cancel</Button>
                <Button
                    onClick={handleUploadFile}
                    variant="contained"
                    disabled={!isFileValid}
                    startIcon={<UploadFileIcon />}
                >
                    Upload
                </Button>
            </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
            open={deleteConfirmDialog}
            onClose={handleCancelDelete}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle>Remove License</DialogTitle>
            <DialogContent>
                <Typography>
                    Are you sure you want to remove the license?
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelDelete}>Cancel</Button>
                <Button
                    onClick={handleConfirmDelete}
                    variant="contained"
                    color="error"
                >
                    Remove
                </Button>
            </DialogActions>
        </Dialog>
    </>);
};

export default LicenseCtrl;
